import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { AppIcon, AppIconName } from '../../icons/appIcons.tsx';

interface Admin {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isSuperAdmin: boolean;
  isActive: boolean;
  adminPermissions?: Permission[];
}

interface Permission {
  id: string;
  permissionKey: string;
  canRead: boolean;
  canWrite: boolean;
  canDelete: boolean;
}

const PERMISSION_SECTIONS: { key: string; label: string; icon: AppIconName }[] = [
  { key: 'families', label: 'Manage Families', icon: 'families' },
  { key: 'ranchgirls', label: 'Ranch Girl Profiles', icon: 'ranchGirl' },
  { key: 'horses', label: 'Manage Horses', icon: 'horse' },
  { key: 'boardmembers', label: 'Board Members', icon: 'board' },
  { key: 'alumni', label: 'Alumni Members', icon: 'members' },
  { key: 'events', label: 'Events & Calendar', icon: 'events' },
  { key: 'duties', label: 'Duty List', icon: 'duties' },
  { key: 'notifications', label: 'Send Notifications', icon: 'notifications' },
  { key: 'girlscorner', label: "Girls' Corner", icon: 'girlsCorner' },
  { key: 'textmessaging', label: 'Text Messaging', icon: 'textMessage' },
  { key: 'documents', label: 'Document Library', icon: 'documents' },
  { key: 'invoices', label: 'Manage Invoices', icon: 'invoices' },
  { key: 'reports', label: 'Financial Reports', icon: 'reports' },
  { key: 'user-activity', label: 'User Activity', icon: 'userActivity' },
  { key: 'settings', label: 'Admin Settings', icon: 'settings' }
];

export default function AdminManagement() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);
  
  // Create admin form
  const [newAdmin, setNewAdmin] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    isSuperAdmin: false
  });

  // Permissions state
  const [permissions, setPermissions] = useState<Record<string, { canRead: boolean; canWrite: boolean; canDelete: boolean }>>({});

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/admin-management/admins', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setAdmins(data.admins);
      } else {
        setError(data.message || 'Failed to load admins');
      }
    } catch (err) {
      setError('Error loading admin users');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/admin-management/create-admin', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newAdmin)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Admin user created successfully!');
        setShowCreateModal(false);
        setNewAdmin({ email: '', password: '', firstName: '', lastName: '', isSuperAdmin: false });
        loadAdmins();
      } else {
        setError(data.message || 'Failed to create admin');
      }
    } catch (err) {
      setError('Error creating admin user');
      console.error('Error:', err);
    }
  };

  const handleManagePermissions = async (admin: Admin) => {
    setSelectedAdmin(admin);
    setError('');

    // Load current permissions
    const permissionsObj: Record<string, { canRead: boolean; canWrite: boolean; canDelete: boolean }> = {};
    
    if (admin.adminPermissions) {
      admin.adminPermissions.forEach(perm => {
        permissionsObj[perm.permissionKey] = {
          canRead: perm.canRead,
          canWrite: perm.canWrite,
          canDelete: perm.canDelete
        };
      });
    }

    setPermissions(permissionsObj);
    setShowPermissionsModal(true);
  };

  const handleToggleSuperAdmin = async (isSuperAdmin: boolean) => {
    if (!selectedAdmin) return;

    // Prevent demoting yourself
    if (selectedAdmin.id === user?.id && !isSuperAdmin) {
      setError('You cannot remove your own super admin privileges');
      return;
    }

    try {
      setError('');
      const token = localStorage.getItem('authToken');

      const response = await fetch(`/api/admin-management/update-admin/${selectedAdmin.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isSuperAdmin })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Update local state
        setSelectedAdmin({ ...selectedAdmin, isSuperAdmin });
        setSuccess(isSuperAdmin ? 'Promoted to Super Admin!' : 'Changed to Regular Admin');
        
        // If demoting from super admin, clear permissions
        if (!isSuperAdmin) {
          setPermissions({});
        }
        
        loadAdmins();
      } else {
        setError(data.message || 'Failed to update admin status');
      }
    } catch (err) {
      setError('Error updating admin status');
      console.error('Error:', err);
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedAdmin) return;

    try {
      setError('');
      const token = localStorage.getItem('authToken');
      
      // Convert permissions object to array
      const permissionsArray = Object.entries(permissions).map(([key, value]) => ({
        permissionKey: key,
        ...value
      }));

      const response = await fetch(`/api/admin-management/permissions/${selectedAdmin.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ permissions: permissionsArray })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Permissions updated successfully!');
        setShowPermissionsModal(false);
        loadAdmins();
      } else {
        setError(data.message || 'Failed to update permissions');
      }
    } catch (err) {
      setError('Error updating permissions');
      console.error('Error:', err);
    }
  };

  const handleToggleSection = (permissionKey: string) => {
    setPermissions(prev => {
      const exists = prev[permissionKey];
      if (exists) {
        // Remove permission (turn off access)
        const newPerms = { ...prev };
        delete newPerms[permissionKey];
        return newPerms;
      } else {
        // Add permission (turn on access)
        return {
          ...prev,
          [permissionKey]: {
            canRead: true,
            canWrite: true,
            canDelete: false
          }
        };
      }
    });
  };

  const handleDeactivateAdmin = async (adminId: string) => {
    if (!window.confirm('Are you sure you want to deactivate this admin user?')) {
      return;
    }

    try {
      setError('');
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/admin-management/admin/${adminId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Admin user deactivated successfully');
        loadAdmins();
      } else {
        setError(data.message || 'Failed to deactivate admin');
      }
    } catch (err) {
      setError('Error deactivating admin user');
      console.error('Error:', err);
    }
  };

  const handleActivateAdmin = async (adminId: string) => {
    if (!window.confirm('Are you sure you want to activate this admin user?')) {
      return;
    }

    try {
      setError('');
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/admin-management/admin/${adminId}/activate`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Admin user activated successfully');
        loadAdmins();
      } else {
        setError(data.message || 'Failed to activate admin');
      }
    } catch (err) {
      setError('Error activating admin user');
      console.error('Error:', err);
    }
  };

  const handlePermanentDelete = async (admin: Admin) => {
    const confirmMsg = `⚠️ PERMANENT DELETE\n\nAre you sure you want to PERMANENTLY delete ${admin.firstName} ${admin.lastName} (${admin.email})?\n\nThis action CANNOT be undone!`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }

    // Double confirm with typed verification
    const confirmation = prompt('Type "DELETE" to confirm permanent deletion:');
    if (confirmation !== 'DELETE') {
      alert('Deletion cancelled. You must type "DELETE" exactly to confirm.');
      return;
    }

    try {
      setError('');
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/admin-management/admin/${admin.id}/permanent`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Admin user permanently deleted');
        loadAdmins();
      } else {
        setError(data.message || 'Failed to delete admin');
      }
    } catch (err) {
      setError('Error deleting admin user');
      console.error('Error:', err);
    }
  };

  if (!user?.isSuperAdmin) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">⚠️ You must be a Super Admin to access this section.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Management</h1>
        <p className="text-gray-600">Manage admin users and their permissions</p>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700">{success}</p>
        </div>
      )}

      {/* Create Admin Button */}
      <div className="mb-6">
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-dark"
        >
          + Create New Admin
        </button>
      </div>

      {/* Admins List */}
      {loading ? (
        <div className="text-center py-8">
          <p className="text-gray-600">Loading...</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Permissions</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {admins.map((admin) => (
                <tr key={admin.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {admin.firstName} {admin.lastName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{admin.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      admin.isSuperAdmin 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {admin.isSuperAdmin ? '⭐ Super Admin' : 'Admin'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      admin.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {admin.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {admin.isSuperAdmin ? (
                      'Full Access'
                    ) : (
                      `${admin.adminPermissions?.length || 0} sections`
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                    <button
                      onClick={() => handleManagePermissions(admin)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Manage Permissions
                    </button>
                    {admin.id !== user?.id && admin.isActive && (
                      <button
                        onClick={() => handleDeactivateAdmin(admin.id)}
                        className="text-red-600 hover:text-red-800 ml-2"
                      >
                        Deactivate
                      </button>
                    )}
                    {admin.id !== user?.id && !admin.isActive && (
                      <button
                        onClick={() => handleActivateAdmin(admin.id)}
                        className="text-green-600 hover:text-green-800 ml-2"
                      >
                        Activate
                      </button>
                    )}
                    {admin.id !== user?.id && (
                      <button
                        onClick={() => handlePermanentDelete(admin)}
                        className="text-red-800 hover:text-red-900 ml-2 font-medium"
                        title="Permanently delete this admin"
                      >
                        🗑️ Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Admin Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold mb-4">Create New Admin</h2>
            <form onSubmit={handleCreateAdmin}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={newAdmin.email}
                    onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    value={newAdmin.password}
                    onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                    minLength={6}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input
                    type="text"
                    value={newAdmin.firstName}
                    onChange={(e) => setNewAdmin({ ...newAdmin, firstName: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={newAdmin.lastName}
                    onChange={(e) => setNewAdmin({ ...newAdmin, lastName: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isSuperAdmin"
                    checked={newAdmin.isSuperAdmin}
                    onChange={(e) => setNewAdmin({ ...newAdmin, isSuperAdmin: e.target.checked })}
                    className="mr-2"
                  />
                  <label htmlFor="isSuperAdmin" className="text-sm text-gray-700">
                    Make Super Admin (full access to everything)
                  </label>
                </div>
              </div>
              <div className="mt-6 flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-dark"
                >
                  Create Admin
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {showPermissionsModal && selectedAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 my-8">
            <h2 className="text-2xl font-bold mb-4">
              Manage Permissions: {selectedAdmin.firstName} {selectedAdmin.lastName}
            </h2>
            
            {/* Super Admin Toggle */}
            <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900 flex items-center">
                    <span className="mr-2">⭐</span>
                    Super Admin Status
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedAdmin.isSuperAdmin 
                      ? 'This user has full access to all sections' 
                      : 'Grant this user full access to all sections'}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedAdmin.isSuperAdmin}
                    onChange={(e) => handleToggleSuperAdmin(e.target.checked)}
                    disabled={selectedAdmin.id === user?.id}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                </label>
              </div>
              {selectedAdmin.id === user?.id && (
                <p className="text-xs text-gray-500 mt-2 italic">
                  You cannot change your own super admin status
                </p>
              )}
            </div>

            {!selectedAdmin.isSuperAdmin && (
              <>
                <p className="text-gray-600 mb-4">
                  Select which sections this admin can access:
                </p>
                
                <div className="space-y-2 max-h-96 overflow-y-auto">
              {PERMISSION_SECTIONS.map((section) => {
                const hasPermission = permissions[section.key];
                return (
                  <label 
                    key={section.key} 
                    className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      hasPermission 
                        ? 'border-ggaa-green bg-green-50' 
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={!!hasPermission}
                      onChange={() => handleToggleSection(section.key)}
                      className="rounded w-4 h-4"
                    />
                    <AppIcon name={section.icon} className="w-5 h-5 text-gray-500 shrink-0" />
                    <span className="font-medium flex-1">{section.label}</span>
                    {hasPermission && (
                      <span className="text-xs bg-ggaa-green text-white px-2 py-1 rounded">
                        Enabled
                      </span>
                    )}
                  </label>
                );
              })}
                </div>
              </>
            )}

            {!selectedAdmin.isSuperAdmin && (
              <div className="mt-6 flex space-x-3">
                <button
                  onClick={handleSavePermissions}
                  className="flex-1 px-4 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-dark"
                >
                  Save Permissions
                </button>
                <button
                  onClick={() => setShowPermissionsModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            )}
            
            {selectedAdmin.isSuperAdmin && (
              <div className="mt-6">
                <button
                  onClick={() => setShowPermissionsModal(false)}
                  className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

